import { createSelector } from '@reduxjs/toolkit';
import { HierarchyLevelType, IObject } from 'common/interfaces/hierarchy-entities';
import { EntityType } from 'common/enums/entity-enums';
import { RootState } from '../../configureStore';

export const objectsSelector = ({ entities }: RootState) => {
  return entities.object;
};

export const getUnpackState = createSelector(
  objectsSelector,
  ({ preUnpackData, unPackError, isLoadingPreUnpack }) => {
    return {
      preUnpackData, unPackError, isLoadingPreUnpack,
    };
  },
);

export const getObjectsUpdateState = createSelector(
  objectsSelector,
  ({ isLoadingUpdate }) => {
    return isLoadingUpdate;
  },
);

export const getAssociatedObject = createSelector(
  [objectsSelector, (_state, objectid) => objectid],
  ({ associatedObjects }, objectid) => {
    return associatedObjects?.[objectid];
  },
);

export const getObjectsByTypeAndParent = createSelector(
  [objectsSelector, (_state, objecttype, parenttype, parentid) => ({ objecttype, parenttype, parentid })],
  ({ objects }, { objecttype, parenttype, parentid }) => {
    return Object.values(objects)?.reduce((acc: IObject[], objects: IObject[]) => {
      for (const obj of objects) {
        if (obj.type === objecttype && obj.metadata?.hierarchyData?.[parenttype]?.id === parentid) {
          acc.push(obj);
        }
      }
      return acc;
    }, []);
  },
);

const surveyRunLinkedAssociatedDataFields = ['geomagneticParametersDefinition', 'holeSize', 'covarianceMatrix'];

export const getSurveyRunLinkedObjectIds = createSelector(
  [(state: RootState, parentid: string | null) => getObjectsByTypeAndParent(state, EntityType.SurveyRun, HierarchyLevelType.Design, parentid)],
  (surveyRuns) => surveyRuns.reduce((acc: { [key: string]: string }, cur) => {
    surveyRunLinkedAssociatedDataFields.forEach(field => {
      if (cur?.associateddata?.[field]) {
        acc[cur.associateddata[field]] = cur.associateddata.surveyrunsassociateddataid;
      }
    });
    return acc;
  }, {}),
);

export const getSurveyRunsLinkedItemsMap = createSelector(
  [
    (state: RootState, parentid: string | null, _objectid) => getObjectsByTypeAndParent(state, EntityType.SurveyRun, HierarchyLevelType.Design, parentid),
    (_state: RootState, _parentid: string | null, objectid) => objectid
  ],
  (surveyRuns, objectid): {
     [EntityType.HoleSize]: string[],
     [EntityType.CovarianceMatrix]: string[],
     [EntityType.GeomagneticParameters]: string[],
  } => {
    //@ts-ignore
    return surveyRuns.reduce((acc, cur) => {
      const holderHoleSize = [];
      const holderGeomag = [];
      const holderCovarianceMatrix = [];
      if(cur?.associateddata?.holeSize === objectid) {
        holderHoleSize.push(cur.objectid)
      }
      if(cur?.associateddata?.geomagneticParametersDefinition === objectid) {
        holderGeomag.push(cur.objectid)
      }
      if(cur?.associateddata?.covarianceMatrix === objectid) {
        holderCovarianceMatrix.push(cur.objectid)
      }
      return {
        ...acc,
      [EntityType.HoleSize]: [
        ...acc[EntityType.HoleSize],
        ...holderHoleSize,
      ],
      [EntityType.GeomagneticParameters]:[
        ...acc[EntityType.GeomagneticParameters],
        ...holderGeomag,
      ],
      [EntityType.CovarianceMatrix]:[
        ...acc[EntityType.CovarianceMatrix],
        ...holderCovarianceMatrix
      ],
      }
    }, {
       [EntityType.HoleSize]: [],
       [EntityType.CovarianceMatrix]: [],
       [EntityType.GeomagneticParameters]: [],
    })
  },
);

export const getAllAssociatedSurveyRunsOptions = createSelector(
  objectsSelector,
  ({ associatedObjects }) => {
    return Object.values(associatedObjects).reduce((acc, cur) => {
      if (cur.surveyRunName) {
        return [...acc, { value: cur.associatedobjectid, label: cur.surveyRunName }];
      }
      return [...acc];
    }, []);
  },
);
